/* Options → 🐋 Whale Alerts sub-tab (PREVIEW / UPGRADE REQUIRED).

   The user's current Polygon plan does not include the Options snapshot
   endpoint. This component shows a professional preview of what Whale
   Alerts will look like once Polygon Options data is available, using
   realistic example data. A clear upgrade notice explains how to activate.

   When the upgrade happens, flip `isLive` true and wire to a future
   `/api/stock/options/unusual` endpoint. The card components here are
   already written to accept live data shape. */

import { C } from "./_shared";

const DISCLAIMER = (
  "Unusual options activity does not guarantee price movement. Large traders can be wrong. "
  + "Use as one signal among many — never as sole basis for a trade."
);

// ── Realistic example data ──
// Hand-picked to cover each whale tier + both sentiments + common shapes
// you'd actually see on a high-flow day. Flagged `_mock: true` so we can
// later distinguish live-vs-preview rows if needed.
const MOCK_WHALES = [
  {
    ticker: "MU", name: "Micron Technology",
    type: "PUT", sentiment: "BEARISH",
    strike: 465, expiration: "2026-05-08",
    premium: 1_135_650, volume: 18_990, open_interest: 334,
    vol_oi_ratio: 56.9, current_price: 517.13, implied_vol: 45.2,
    dte: 7, strike_distance_pct: -10.1,
    tier: "mega",
    _mock: true,
  },
  {
    ticker: "NVDA", name: "NVIDIA Corporation",
    type: "CALL", sentiment: "BULLISH",
    strike: 210, expiration: "2026-05-17",
    premium: 687_200, volume: 8_590, open_interest: 1_240,
    vol_oi_ratio: 6.9, current_price: 199.88, implied_vol: 52.3,
    dte: 16, strike_distance_pct: 5.1,
    tier: "large",
    _mock: true,
  },
  {
    ticker: "TSLA", name: "Tesla Inc.",
    type: "CALL", sentiment: "BULLISH",
    strike: 400, expiration: "2026-05-24",
    premium: 412_800, volume: 5_100, open_interest: 890,
    vol_oi_ratio: 5.7, current_price: 378.45, implied_vol: 68.7,
    dte: 23, strike_distance_pct: 5.7,
    tier: "whale",
    _mock: true,
  },
  {
    ticker: "AMD", name: "Advanced Micro Devices",
    type: "PUT", sentiment: "BEARISH",
    strike: 335, expiration: "2026-05-10",
    premium: 89_450, volume: 2_140, open_interest: 420,
    vol_oi_ratio: 5.1, current_price: 349.88, implied_vol: 58.9,
    dte: 9, strike_distance_pct: -4.3,
    tier: "shark",
    _mock: true,
  },
];

// Whale tier mapping by premium (per user spec).
//   >= $1M    — 🐋🐋🐋 Mega whale
//   $500k–$1M — 🐋🐋 Large whale
//   $100k–$500k — 🐋 Whale
//   $50k–$100k — 🦈 Shark
function tierMeta(premium) {
  if (premium >= 1_000_000) return { key: "mega",  icon: "🐋🐋🐋", label: "Mega Whale",  color: "#7c3aed", bg: "#ede9fe" };
  if (premium >= 500_000)   return { key: "large", icon: "🐋🐋",   label: "Large Whale", color: "#4f46e5", bg: "#e0e7ff" };
  if (premium >= 100_000)   return { key: "whale", icon: "🐋",     label: "Whale",       color: "#0369a1", bg: "#e0f2fe" };
  return { key: "shark", icon: "🦈", label: "Shark", color: "#059669", bg: "#d1fae5" };
}

const fmtMoney = (n) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
};
const fmtDate = (iso) => {
  try { return new Date(iso + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
  catch { return iso; }
};

export default function OptionsWhaleAlerts() {
  // When the user upgrades Polygon, flip this true and point to the real
  // /api/stock/options/unusual endpoint. The WhaleCard component already
  // accepts the shape described in the user's spec.
  const isLive = false;
  const alerts = MOCK_WHALES;

  return (
    <div data-testid="options-whale-alerts" style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* ── Upgrade banner ────────────────────────────── */}
      {!isLive && (
        <div style={{
          background: "linear-gradient(135deg,#1e1b4b,#312e81)", color: "#fff",
          borderRadius: 10, padding: "18px 22px", position: "relative", overflow: "hidden",
        }} data-testid="whale-alerts-upgrade-banner">
          <div style={{ position: "absolute", top: -30, right: -30, fontSize: 160, opacity: 0.07 }}>🐋</div>
          <div style={{ position: "relative" }}>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 6 }}>
              🔒 Whale Alerts — Polygon Options plan required
            </div>
            <div style={{ fontSize: 12, opacity: 0.85, lineHeight: 1.6, marginBottom: 10, maxWidth: 720 }}>
              Whale Alerts track unusually large institutional options trades — "smart money" positioning. Detecting
              flow above $50k requires Polygon's Options snapshot endpoint, which is not included on the free tier.
              <br/>
              <b style={{ color: "#a5b4fc" }}>Upgrade at <span style={{ textDecoration: "underline" }}>polygon.io/pricing</span> (Options Starter $29/mo)</b> to activate this tab.
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
              {[
                { icon: "⚡",   l: "Real-time flow detection" },
                { icon: "🔄",   l: "Auto-scan every 5 min during market hours" },
                { icon: "📱",   l: "Telegram alert when flow > $100k" },
                { icon: "🎯",   l: "Filter by direction, premium, expiry" },
                { icon: "📊",   l: "Scanner scoring bonus when whale detected" },
              ].map((f, i) => (
                <span key={i} style={{
                  fontSize: 11, padding: "4px 10px", borderRadius: 5,
                  background: "rgba(255,255,255,0.12)", color: "#e0e7ff", fontWeight: 600,
                }}>{f.icon} {f.l}</span>
              ))}
            </div>
            <div style={{
              fontSize: 11, color: "#e0e7ff", fontStyle: "italic",
              padding: "6px 12px", background: "rgba(255,255,255,0.08)", borderRadius: 6,
              display: "inline-block",
            }}>
              The cards below show example data — exactly what you'll see when activated.
            </div>
          </div>
        </div>
      )}

      {/* ── Disclaimer ─────────────────────────────────── */}
      <div style={{
        background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8,
        padding: "10px 14px", fontSize: 11, color: "#78350f", lineHeight: 1.5,
      }} data-testid="whale-alerts-disclaimer">
        ⚠️ <b>Not financial advice.</b> {DISCLAIMER}
      </div>

      {/* ── Tier legend ────────────────────────────────── */}
      <div style={{
        background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10,
        padding: "10px 14px", display: "flex", flexWrap: "wrap", gap: 14,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#111" }}>🐋 Whale tiers:</div>
        {[
          { t: "mega",  l: "Mega Whale",  icon: "🐋🐋🐋", range: ">$1M"       },
          { t: "large", l: "Large Whale", icon: "🐋🐋",    range: "$500K–$1M" },
          { t: "whale", l: "Whale",       icon: "🐋",      range: "$100K–$500K" },
          { t: "shark", l: "Shark",       icon: "🦈",      range: "$50K–$100K" },
        ].map(x => (
          <div key={x.t} style={{ fontSize: 10, color: C.gray }}>
            <span style={{ fontSize: 14, marginRight: 4 }}>{x.icon}</span>
            <b style={{ color: "#111" }}>{x.l}</b> · {x.range}
          </div>
        ))}
      </div>

      {/* ── Example alert cards (4-up on desktop) ────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 10 }}>
        {alerts.map((a, i) => (
          <WhaleCard key={i} alert={a} isLive={isLive}/>
        ))}
      </div>

      {/* ── "What triggers an alert" explainer ──────────── */}
      <div style={{
        background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "14px 16px",
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#111", marginBottom: 8 }}>🔬 Detection criteria</div>
        <div style={{ fontSize: 11, color: C.gray, lineHeight: 1.7 }}>
          A contract is flagged as a <b>whale alert</b> when it satisfies <b>both</b> of:
          <ol style={{ paddingLeft: 20, margin: "6px 0", lineHeight: 1.6 }}>
            <li><b>Vol/OI ratio ≥ 3.0</b> — today's volume at least 3× the open interest → new position being built, not rolling.</li>
            <li><b>Premium ≥ $50,000</b> — contract × price × 100 × volume filters out retail-sized trades.</li>
          </ol>
          <b style={{ color: "#111" }}>Scanner scoring bonus</b>: a ticker with whale activity today earns <b>+3 points</b> in the S&P scanner, plus <b>+2 bonus</b> if the whale sentiment (call vs put) aligns with the strategy's bias.
        </div>
      </div>

    </div>
  );
}

// ── Whale card ───────────────────────────────────────────
function WhaleCard({ alert, isLive }) {
  const tier = tierMeta(alert.premium);
  const isBullish = alert.sentiment === "BULLISH";
  const sentimentColor = isBullish ? "#16a34a" : "#dc2626";
  const sentimentBg    = isBullish ? "#dcfce7" : "#fee2e2";
  const distanceLabel  = `${alert.strike_distance_pct >= 0 ? "+" : ""}${alert.strike_distance_pct.toFixed(1)}% ${Math.abs(alert.strike_distance_pct) > 1 ? "OTM" : "ATM"}`;
  const conviction = alert.premium >= 500_000 ? "HIGH" : alert.premium >= 100_000 ? "MEDIUM" : "LOW";
  const convictionColor = conviction === "HIGH" ? "#dc2626" : conviction === "MEDIUM" ? "#b45309" : "#0369a1";

  return (
    <div
      data-testid={`whale-card-${alert.ticker}`}
      style={{
        background: "#fff",
        border: `1.5px solid ${tier.color}55`, borderRadius: 12,
        overflow: "hidden", position: "relative",
      }}
    >
      {/* Tier badge strip */}
      <div style={{
        background: tier.bg, padding: "8px 14px",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
      }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: tier.color }}>
          {tier.icon} {tier.label.toUpperCase()}
        </div>
        <span style={{
          fontSize: 10, padding: "2px 9px", borderRadius: 4,
          background: sentimentBg, color: sentimentColor, fontWeight: 800, letterSpacing: "0.04em",
        }}>{isBullish ? "🐂" : "🐻"} {alert.sentiment}</span>
      </div>

      {/* Body */}
      <div style={{ padding: "10px 12px" }}>
        <div style={{ fontSize: 16, fontWeight: 900, color: "#111" }}>{alert.ticker}</div>
        <div style={{ fontSize: 11, color: C.gray, marginBottom: 10 }}>{alert.name}</div>

        <div style={{
          fontSize: 12, fontWeight: 700, color: "#111", marginBottom: 10,
          padding: "7px 10px", background: "#f9fafb", borderRadius: 6,
        }}>
          {alert.type} @ ${alert.strike} · Exp {fmtDate(alert.expiration)}
        </div>

        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 10,
        }}>
          <Stat label="Premium" value={fmtMoney(alert.premium)} color={tier.color} big/>
          <Stat label="Vol / OI" value={`${alert.vol_oi_ratio.toFixed(1)}×`} color="#4f46e5" big/>
          <Stat label="Volume"        value={alert.volume.toLocaleString()} />
          <Stat label="Open Interest" value={alert.open_interest.toLocaleString()} />
          <Stat label="Current"       value={`$${alert.current_price.toFixed(2)}`} />
          <Stat label="Distance"      value={distanceLabel} color={alert.strike_distance_pct >= 0 ? "#16a34a" : "#dc2626"}/>
          <Stat label="Implied Vol"   value={`${alert.implied_vol.toFixed(1)}%`} />
          <Stat label="DTE"           value={`${alert.dte}d`} color={alert.dte < 7 ? "#dc2626" : "#111"}/>
        </div>

        <div style={{
          background: "#f9fafb", borderRadius: 6, padding: "8px 10px", fontSize: 11, color: C.gray, lineHeight: 1.6,
        }}>
          <div>
            <b style={{ color: "#111" }}>Signal:</b> {alert.vol_oi_ratio > 5 ? "NEW position — vol far exceeds OI" : "Position building"} · {alert.dte}d to expiry
          </div>
          <div>
            <b style={{ color: "#111" }}>Conviction:</b>{" "}
            <span style={{ color: convictionColor, fontWeight: 700 }}>{conviction}</span>
            {conviction === "HIGH" && " — institutional-sized"}
          </div>
        </div>

        {!isLive && (
          <div style={{
            marginTop: 8, padding: "5px 8px", background: "#fef3c7", borderRadius: 4,
            fontSize: 9, color: "#78350f", fontWeight: 700, textAlign: "center", letterSpacing: "0.04em",
          }}>
            EXAMPLE DATA · ACTIVATES WITH POLYGON OPTIONS PLAN
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color, big }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: C.gray, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: big ? 14 : 11, fontWeight: big ? 800 : 700, color: color || "#111", marginTop: 1 }}>{value}</div>
    </div>
  );
}
